import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");

  const activeCategory = params.get("category") || "All";

  useEffect(() => {
    setLoading(true);
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (activeCategory !== "All") list = list.filter((p) => p.category === activeCategory);
    if (search)
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          p.short_description.toLowerCase().includes(search.toLowerCase())
      );
    return list;
  }, [products, activeCategory, search]);

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="mb-12">
        <p className="overline">Shop</p>
        <h1 className="mt-2 font-serif text-5xl sm:text-6xl text-[#1e392a] tracking-tight">
          The full apothecary.
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3 space-y-8">
          <div>
            <p className="overline mb-3">Search</p>
            <Input
              data-testid="shop-search-input"
              placeholder="Coconut, castor…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-white border-[#e6e2d8]"
            />
          </div>

          <div>
            <p className="overline mb-3">Category</p>
            <ul className="space-y-2">
              {["All", ...categories].map((c) => (
                <li key={c}>
                  <button
                    data-testid={`filter-category-${c.replace(/\s/g, "-").toLowerCase()}`}
                    onClick={() => {
                      if (c === "All") {
                        params.delete("category");
                      } else {
                        params.set("category", c);
                      }
                      setParams(params);
                    }}
                    className={
                      "text-sm py-1 transition-colors " +
                      (activeCategory === c
                        ? "text-[#c36a4f] font-semibold"
                        : "text-[#1a1b1a]/70 hover:text-[#1e392a]")
                    }
                  >
                    {c}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="lg:col-span-9">
          <p data-testid="shop-count" className="text-xs text-[#8a8d86] mb-6">
            {loading ? "Loading…" : `${filtered.length} product${filtered.length === 1 ? "" : "s"}`}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
          {!loading && filtered.length === 0 && (
            <div className="text-center py-16 border border-dashed border-[#e6e2d8] rounded-md">
              <p className="text-sm text-[#8a8d86]">No products match your filters.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
